import classNames from "classnames";
import { Loader } from "common/Loader";
import { useRouter } from "hooks/useRouter";
import { formatPhone } from "pages/Contacts";
import { tabStyles } from "pages/GeneralData";
import { Fragment, useCallback, useEffect, useState } from "react";
import UserService from "services/user.service";
import { exportToCSV, formatDate } from "utils/funcs";

const service = new UserService();

export const DealerTrainingDetails = () => {
  const { params, query, push } = useRouter();

  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [tab, setTab] = useState("Attendees");
  const [type, setType] = useState("screen");
  const [attendees, setAttendees] = useState([]);
  const [rsvp, setRsvp] = useState([]);
  const [screeningData, setScreeningData] = useState([]);

  const [data, setData] = useState<any>(null);

  const fetchScreeningDetails = useCallback(() => {
    setLoadingUsers(true)
    service
      .getScreenDetails(data?.socialMediaAdId, type)
      .then((resp) => {
        setScreeningData(resp.data.data)
        setLoadingUsers(false)
      })
      .catch((error) => {
        console.log(error)
      })
  }, [data, type])


  const fetchData = useCallback(() => {
    setLoading(true)

    service
      .getDealerDetails(params?.id, query?.connectType)
      .then((resp) => {
        if (resp.data.success) {
          setData(resp.data.data[0])
          if (query?.connectType !== "Interested Candidates") {
            setRsvp(resp.data.data[0].attendees);
            setAttendees(
              resp.data.data[0].attendees.filter((item: any) => item.isAttended)
            )
          }
        }
        setLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setData([])
      })
      
  }, [params, query])

  const handleExport = () => {
    const dataToExport = tab === "Attendees" ? attendees : rsvp
    exportToCSV(dataToExport, "data")
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    fetchScreeningDetails()
  }, [fetchScreeningDetails, type])

  const handleClick = (item) => {
    const apiData = {
      training_id: item.training_id,
      first_name: item.first_name,
      last_name: item.last_name,
      phone: item.phone,
      email: item.email,
      id: item._id,
      badgeId: data?.badge_id,
    };

    service
      .updateRSVP(apiData)
      .then((resp) => {
        fetchData();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleClickNotAttended = (item) => {
    const apiData = {
      status: item?.status === "RSVP" ? "Not-Attended" : "RSVP",
      id: item._id,
    };

    service
      .updateRSVPStatus(apiData)
      .then((resp) => {
        fetchData();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Fragment>
      <div className="flex items-center gap-3 ">
        <button
          onClick={() => push("/dealer-trainings")}
          className="btn-secondary"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
            />
          </svg>
        </button>
        <div>
          <h1 className="page-title">
            Dealer Training
          </h1>
          <p className="page-subtitle">View dealer training details</p>
        </div>
      </div>
      <div className="flex gap-4">
        <div className="w-1/4">
          <p className="mt-4 text-lg font-semibold">Training Details</p>

         {query?.connectType === "Interested Candidates" && <div className="flex flex-col gap-2">
            <div className="card mt-1.5 flex flex-col px-4 py-2 divide-y divide-line">
               <div
                onClick={() => setType("applicant")}
                className="flex justify-between gap-4 py-3 cursor-pointer"
              >
                <span className={classNames({
                  "text-green-500": type === "applicant"
                })}>Applicant</span>
                <span>{data?.applicantCount}</span>
              </div>
              <div
                onClick={() => setType("screening")}
                className="flex justify-between gap-4 py-3 cursor-pointer"
              >
                <span className={classNames({
                  "text-green-500": type === "screening"
                })}>Screened</span>
                <span>{data?.screenCount}</span>
              </div>
              <div
                onClick={() => setType("contacted")}
                className="flex justify-between gap-4 py-3 cursor-pointer"
              >
                <span className={classNames({
                  "text-green-500": type === "contacted"
                })}>Contacted</span>
                <span>{data?.screenContactedCount}</span>
              </div>
              <div
                onClick={() => setType("unAbleToContact")}
                className="flex justify-between gap-4 py-3 cursor-pointer"
              >
                <span className={classNames({
                  "text-green-500": type === "unAbleToContact"
                })}>Unable to Contact</span>
                <span>{data?.unableToContactCount}</span>
              </div>
              <div
                onClick={() => setType("accepted")}
                className="flex justify-between gap-4 py-3 cursor-pointer"
              >
                <span className={classNames({
                  "text-green-500": type === "accepted"
                })}>Accepted</span>
                <span>{data?.screenAcceptedCount}</span>
              </div>
              <div
                onClick={() => setType("rejected")}
                className="flex justify-between gap-4 py-3 cursor-pointer"
              >
                <span className={classNames({
                  "text-green-500": type === "rejected"
                })}>Rejected</span>
                <span>{data?.screenRejectedCount}</span>
              </div>
            </div>
            <div className="card mt-1.5 flex flex-col px-4 py-2 divide-y divide-line">
              <div className="flex justify-between gap-4 py-3">
                <span>Connect Type</span>
                <span>{data?.connectType}</span>
              </div>

              <div className="flex justify-between gap-4 py-3">
                <span>Connect Format</span>
                <span>{data?.connectFormat}</span>
              </div>
              <div className="flex justify-between gap-4 py-3">
                <span>Source</span>
                <span>{data?.source}</span>
              </div>
              <div className="flex justify-between gap-4 py-3">
                <span>Leader</span>
                <span>{data?.createdBy}</span>
              </div>
              {data?.type === "Weekly Training" && (
                <div className="flex justify-between gap-4 py-3">
                  <span>Training Title</span>
                  <span>{data?.trainer_name}</span>
                </div>
              )}
              {/*  <div className="flex justify-between gap-4 py-3">
                <span>Training Title</span>
                <span>{data?.title}</span>
              </div> */}
            </div>
          </div>}
          <div className="card mt-1.5 flex flex-col px-4 py-2 divide-y divide-line">
            <div className="flex justify-between gap-4 py-3">
              <span>Meeting Date</span>
              <span>{formatDate(data?.meetingDate)}</span>
            </div>
            {query?.connectType !== "Interested Candidates" && (
              <div className="flex justify-between gap-4 py-3">
                <span>Start Time</span>
                <span>{data?.meetingStartTime}</span>
              </div>
            )}
            {query?.connectType !== "Interested Candidates" && (
              <div className="flex justify-between gap-4 py-3">
                <span>End Time</span>
                <span>{data?.meetingEndTime}</span>
              </div>
            )}
            {query?.connectType !== "Interested Candidates" && (
              <div className="flex justify-between gap-4 py-3">
                <span>Timezone</span>
                <span>{data?.timeZone}</span>
              </div>
            )}
          </div>
          {query?.connectType !== "Interested Candidates" && (
            <div className="card mt-1.5 flex flex-col px-4 py-2 divide-y divide-line">
              <div className="flex justify-between gap-4 py-3">
                <span>Location</span>
                <span>
                  {data?.connectFormat === "Virtual Meeting"
                    ? "Virtual Meeting"
                    : data?.location}
                </span>
              </div>
              {(data?.connectFormat === "Group Meeting" ||
                data?.connectFormat === "Virtual Meeting") && (
                <div className="flex justify-between gap-4 py-3">
                  <span>Meeting URL</span>
                  <span className="break-all">{data?.meetingLink}</span>
                </div>
              )}
              {data?.location && (
                <div className="flex justify-between gap-4 py-3">
                  <span>Address</span>
                  <span className="break-all">{data?.address}</span>
                </div>
              )}
              {data?.location && (
                <div className="flex justify-between gap-4 py-3">
                  <span>City</span>
                  <span>{data?.city}</span>
                </div>
              )}
              {data?.location && (
                <div className="flex justify-between gap-4 py-3">
                  <span>State</span>
                  <span>{data?.state}</span>
                </div>
              )}
              {data?.location && (
                <div className="flex justify-between gap-4 py-3">
                  <span>Zip Code</span>
                  <span>{data?.zip_code}</span>
                </div>
              )}
              {(data?.type === "Campus Reel" ||
                data?.type === "Social Media Ad") && (
                <div className="flex justify-between gap-4 py-3">
                  <span>Meeting URL</span>
                  <span>{data?.meet_link}</span>
                </div>
              )}
            </div>
          )}
          <div className="card mt-1.5 flex flex-col px-4 py-2 divide-y divide-line">
            <div className="flex justify-between gap-4 py-3">
              <span>Created By</span>
              <span>
                {data?.createdBy ? data?.createdBy : data?.creator_name}
              </span>
            </div>
            <div className="flex justify-between gap-4 py-3">
              <span>Creation Date</span>
              <span className="break-all">{formatDate(data?.createdAt)}</span>
            </div>
          </div>
        </div>

        {query?.connectType !== "Interested Candidates" ? (
          <div className="flex-1 mt-[52px]">
            <div className="flex items-end justify-end ">
              <button
                type="button"
                onClick={handleExport}
                className="bg-ink hover:bg-gray-800 text-sm disabled:bg-opacity-40 text-white  px-4 py-2.5 rounded-lg"
              >
                Export {tab}
              </button>
            </div>
            <div className="p-2 mt-2 mb-4 text-sm text-center text-gray-500 bg-gray-200 rounded-md">
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => setTab("Attendees")}
                  className={classNames(
                    `${tabStyles.default} flex items-center justify-center gap-2`,
                    {
                      [tabStyles.active]: tab === "Attendees",
                    }
                  )}
                >
                  <span>Attendees</span>
                  <span className="flex-shrink-0 px-2 py-1 text-xs bg-gray-200 rounded-full">
                    {attendees?.length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setTab("RSVP")}
                  className={classNames(
                    `${tabStyles.default} flex items-center justify-center gap-2`,
                    {
                      [tabStyles.active]: tab === "RSVP",
                    }
                  )}
                >
                  <span>RSVP</span>
                  <span className="flex-shrink-0 px-2 py-1 text-xs bg-gray-200 rounded-full">
                    {data?.rsvpCount}
                  </span>
                </button>
              </div>
            </div>

            {tab === "Attendees" ? (
              <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
                <table className="w-full text-sm text-left text-gray-500 rtl:text-right dark:text-gray-400">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="px-6 py-3">
                        Name
                      </th>

                      <th scope="col" className="px-6 py-3">
                        Phone
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Email
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Status
                      </th>
                      <th scope="col" className="py-3 ">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                    attendees.map((item: any, index) => (
                      <tr
                        key={index}
                        className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                      >
                        <th
                          scope="row"
                          className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                        >
                          {item?.firstName + " " + item.lastName}
                        </th>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {formatPhone(item?.phone)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item?.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item?.attendeesStatus}
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              // onClick={() => handleUpdate(item._id, "Deny")}
                              className={classNames(
                                "rounded-md text-xs px-3 py-2 border border-zinc-300",
                                {
                                  "bg-primary text-white":
                                    item.attendeesStatus === "Deny",
                                }
                              )}
                            >
                              No
                            </button>
                            {/* <button
                            type="button"
                            onClick={() => handleUpdate(item._id, "Follow-up")}
                            className={classNames("rounded-md text-xs px-3 py-2 border border-zinc-300", {
                              "bg-orange-400 text-white": item.status === "Follow-up"
                            })}
                          >
                            Follow-up
                          </button> */}
                            <button
                              type="button"
                              // onClick={() => handleUpdate(item._id, "Hire")}
                              className={classNames(
                                "rounded-md text-xs px-3 py-2 border border-zinc-300",
                                {
                                  "bg-green-500 text-white":
                                    item.attendeesStatus === "Hire",
                                }
                              )}
                            >
                              Yes
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
                <table className="w-full text-sm text-left text-gray-500 rtl:text-right dark:text-gray-400">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="px-6 py-3">
                        Name
                      </th>

                      <th scope="col" className="px-6 py-3 ">
                        Phone
                      </th>
                      <th scope="col" className="px-6 py-3 ">
                        Email
                      </th>
                      <th scope="col" className="px-6 py-3 ">
                        Meeting Time
                      </th>
                      <th scope="col" className="py-3 ">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rsvp.map((item: any, index) => (
                      <tr
                        key={index}
                        className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                      >
                        <th
                          scope="row"
                          className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                        >
                          {item?.firstName + " " + item.lastName}
                        </th>
                        <td className="px-6 py-4 ">
                          {formatPhone(item?.phone)}
                        </td>
                        <td className="px-6 py-4">{item?.email}</td>
                        <td className="px-6 py-4">
                          {item?.meetingStartTime +
                            " - " +
                            item?.meetingEndTime}
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleClick(item)}
                              className={classNames(
                                "px-3 py-2 text-xs text-gray-800 border border-black  rounded-md",
                                {
                                  "border-green-600":
                                    item.rsvpStatus === "Attended",
                                }
                              )}
                            >
                              Attended
                            </button>
                            <button
                              type="button"
                              onClick={() => handleClickNotAttended(item)}
                              className={classNames(
                                "px-3 py-2 text-xs text-gray-800 border border-black  rounded-md",
                                {
                                  "border-primary":
                                    item.rsvpStatus === "Not-Attended",
                                }
                              )}
                            >
                              Not Attended
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 mt-[52px]">
            <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
              <table className="w-full text-sm text-left text-gray-500 rtl:text-right dark:text-gray-400">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-6 py-3">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Phone
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Email
                    </th>
                   {type === "rejected" && <th scope="col" className="px-6 py-3">
                      Rejection Reason
                    </th>
                   }
                  </tr>
                </thead>
                <tbody>
                  {loadingUsers ? 
                    <tr> 
                      <td colSpan={3} className="py-4 text-center">
                        <Loader />
                      </td>
                    </tr> : screeningData.map((item: any, index) => (
                    <tr
                      key={index}
                      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                    >
                      <th
                        scope="row"
                        className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                      >
                        {item?.full_name}
                      </th>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {formatPhone(item?.phone)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item?.email}
                      </td>
                      {type === "rejected" && <td className="px-6 py-4 whitespace-nowrap">
                          {item?.interview?.notes}
                        </td>
                    }
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
};
